// Health appointments are EVENTS (owner, 2026-10-05): personal domain, Health
// area, with `event.appointment` marking them. Pure helpers for the Health tab's
// form and for booking the next one of a repeating appointment.

import type { Event } from '../types';

export type AppointmentRecurrence = NonNullable<Event['appointment']>['recurrence'];

const MONTHS: Record<AppointmentRecurrence, number> = { none: 0, monthly: 1, quarterly: 3, yearly: 12 };

/** Lengths offered by the form, in minutes. */
export const APPOINTMENT_LENGTHS = [15, 30, 45, 60, 90, 120] as const;

export const HEALTH_AREA_ID = 'health';

/**
 * `iso` + `months`, clamped to the month's last day — so a monthly appointment on
 * Jan 31 lands on Feb 28 (or 29), not Mar 3, and a yearly one on Feb 29 lands on
 * Feb 28 the next year.
 */
export function addMonthsClamped(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

const toMin = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
const toHHMM = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** Start + length → end time, kept inside the day (an event can't cross midnight). */
export function appointmentEndTime(start: string, minutes: number): string {
  return toHHMM(Math.min(toMin(start) + minutes, 23 * 60 + 59));
}

/** The appointment's length in minutes (for showing / repeating it). */
export function appointmentMinutes(event: Pick<Event, 'startTime' | 'endTime'>): number {
  return Math.max(0, toMin(event.endTime) - toMin(event.startTime));
}

/**
 * The next booking of a repeating appointment — same title, time, length, notes,
 * place and area, one period later, not yet done — or null when it doesn't repeat
 * or has already booked its next one.
 */
export function nextAppointment(event: Event): Omit<Event, 'id'> | null {
  const rec = event.appointment?.recurrence ?? 'none';
  if (rec === 'none' || event.appointment?.nextId) return null;
  return {
    title: event.title,
    description: event.description,
    location: event.location,
    category: event.category,
    discipline: event.discipline,
    date: addMonthsClamped(event.date, MONTHS[rec]),
    startTime: event.startTime,
    endTime: event.endTime,
    completed: false,
    appointment: { recurrence: rec },
  };
}
